import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import JobsList from './JobsList';
import './ContactRescue.css';

function ContactRescue() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [email, setEmail] = useState('');
  const [planType, setPlanType] = useState('CR');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [emailError, setEmailError] = useState('');

  // Load jobs from Supabase on component mount
  useEffect(() => {
    if (user) {
      loadJobs();
    }
  }, [user]);

  const loadJobs = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('contact_rescue_jobs')
        .select(`
          *,
          file_result,
          file_emails
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Transform data to match JobsList expectations
      const transformedJobs = (data || []).map(job => ({
        ...job,
        // Map database fields to component expectations
        id: job.id,
        email: job.email,
        status: job.status,
        plan_type: job.plan_type,
        createdAt: job.created_at,
        completedAt: job.completed_at,
        contactsFound: job.contacts_found || 0,
        progress: job.progress || 0,
        // Keep download file URLs
        file_result: job.file_result,
        file_emails: job.file_emails
      }));
      
      setJobs(transformedJobs);
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError('Failed to load jobs');
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Returns { ok: boolean, provider?: 'gmail'|'office365', reason?: string }.
  // A user typically has Gmail OR Office365 (not both) — accept if EITHER
  // provider's inbox is currently reachable. Surfaces a per-provider reason
  // when both fail so the admin knows whether to ask the user to reconnect
  // or to sign in for the first time.
  const checkInboxAccess = async (email) => {
    let data;
    try {
      const response = await fetch('/api/check-inbox-by-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
    } catch (err) {
      console.error('Inbox check error:', err);
      throw new Error('Failed to validate inbox access');
    }

    const g = data.google || {};
    const o = data.office365 || {};
    if (g.can_access_inbox === true) return { ok: true, provider: 'gmail' };
    if (o.can_access_inbox === true) return { ok: true, provider: 'office365' };

    if (data.user_found === false) {
      return { ok: false, reason: 'No Evercontact account found for this email.' };
    }
    const noneConnected = g.has_refresh_token === false && o.has_refresh_token === false;
    if (noneConnected) {
      return { ok: false, reason: 'No inbox connected. The user must sign in with Gmail or Office365 first.' };
    }
    const parts = [];
    if (g.has_refresh_token) parts.push(`Gmail: ${g.error || 'token invalid/expired'}`);
    if (o.has_refresh_token) parts.push(`Office365: ${o.error || 'token invalid/expired'}`);
    return {
      ok: false,
      reason: (parts.length ? parts.join(' · ') + ' — user must reconnect.' : 'No working mailbox connection.'),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');

    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!user) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);

    try {
      // Validate inbox access first (Gmail OR Office365)
      const inbox = await checkInboxAccess(email);
      if (!inbox.ok) {
        setEmailError(inbox.reason || 'No working mailbox connection for this user.');
        setLoading(false);
        return;
      }
      console.log(`Launching Contact Rescue for ${email} via ${inbox.provider}`);

      // Create job in Supabase with user_id
      const { data, error } = await supabase
        .from('contact_rescue_jobs')
        .insert([
          {
            user_id: user.id,
            email: email,
            plan_type: planType,
            job_name: `Contact Rescue for ${email}`,
            status: 'pending',
            progress: 0,
            contacts_found: 0,
            contacts_rescued: 0,
            created_at: new Date().toISOString()
          }
        ])
        .select();

      if (error) throw error;

      // Refresh jobs list
      await loadJobs();
      setEmail('');
      setPlanType('CR');
      
    } catch (err) {
      console.error('Error creating job:', err);
      if (err.message?.includes('validate inbox')) {
        setEmailError('Inbox check failed. Please try again.');
      } else {
        setError('Failed to create Contact Rescue job. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      const { error } = await supabase
        .from('contact_rescue_jobs')
        .update({ status: 'failed' })
        .eq('id', jobId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Refresh jobs list
      await loadJobs();
    } catch (err) {
      console.error('Error cancelling job:', err);
      setError('Failed to cancel job. Please try again.');
    }
  };

  const getFilteredJobs = () => {
    switch (activeTab) {
      case 'in_progress':
        return jobs.filter(job => job.status === 'pending' || job.status === 'processing');
      case 'completed':
        return jobs.filter(job => job.status === 'completed');
      default:
        return jobs;
    }
  };

  const totalJobs = jobs.length;
  const inProgressJobs = jobs.filter(job => job.status === 'pending' || job.status === 'processing').length;
  const completedJobs = jobs.filter(job => job.status === 'completed').length;
  const totalContactsFound = jobs.reduce((sum, job) => sum + (job.contactsFound || 0), 0);

  return (
    <div className="contact-rescue">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{totalJobs}</div>
          <div className="stat-label">Total Jobs</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{inProgressJobs}</div>
          <div className="stat-label">In Progress</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{completedJobs}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{totalContactsFound.toLocaleString()}</div>
          <div className="stat-label">Contacts Found</div>
        </div>
      </div>

      <div className="rescue-form-container">
        <h2>Launch Contact Rescue</h2>
        <form onSubmit={handleSubmit} className="rescue-form">
          <div className="form-group">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value.toLowerCase());
                setEmailError('');
                setError('');
              }}
              placeholder="Enter user email address"
              className={`email-input ${emailError ? 'error' : ''}`}
              disabled={loading}
            />
            {emailError && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {emailError}
              </div>
            )}
          </div>
          
          <div className="form-group plan-selection">
            <label className="plan-label">Choose Plan:</label>
            <div className="plan-options">
              <label className={`plan-option ${planType === 'CR' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="CR"
                  checked={planType === 'CR'}
                  onChange={(e) => setPlanType(e.target.value)}
                  disabled={loading}
                />
                <span className="plan-details">
                  <span className="plan-name">CR (2 Years)</span>
                  <span className="plan-description">Standard contact rescue</span>
                </span>
              </label>
              <label className={`plan-option ${planType === 'CR+' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="CR+"
                  checked={planType === 'CR+'}
                  onChange={(e) => setPlanType(e.target.value)}
                  disabled={loading}
                />
                <span className="plan-details">
                  <span className="plan-name">CR+ (5 Years)</span>
                  <span className="plan-description">Extended contact rescue</span>
                </span>
              </label>
            </div>
          </div>
          <button 
            type="submit" 
            className={`launch-button ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? 'Verifying & Launching...' : `Launch ${planType} Rescue Job`}
          </button>
        </form>
        
        {error && (
          <div className="error-message global-error">
            <span className="error-icon">❌</span>
            {error}
          </div>
        )}
      </div>

      <div className="jobs-section">
        <div className="jobs-header">
          <h2>My Contact Rescue Jobs</h2>
          <div className="tab-buttons">
            <button 
              className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All ({totalJobs})
            </button>
            <button 
              className={`tab-button ${activeTab === 'in_progress' ? 'active' : ''}`}
              onClick={() => setActiveTab('in_progress')}
            >
              In Progress ({inProgressJobs})
            </button>
            <button 
              className={`tab-button ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              Completed ({completedJobs})
            </button>
          </div>
        </div>

        <JobsList jobs={getFilteredJobs()} onCancelJob={handleCancelJob} />
      </div>
    </div>
  );
}

export default ContactRescue;